import axios from '@/plugins/axios.js';
import { merge } from '@/utils/index.js';
import { useContextStore } from '@/store/modules/context/store.ts';
import InvalidPayload from '../../../exceptions/InvalidPayload.js';
import Lead from '../../../entities/Lead.js';

const associatedLeadsRequests = new WeakMap();
const associatedLeadsRequestGenerations = new WeakMap();

export default {
    setCustomerId(customerId) {
        const activeRequest = associatedLeadsRequests.get(this);

        if (activeRequest?.customerId === customerId) {
            return activeRequest.promise;
        }

        const contextLeadId = useContextStore().leadId;
        const contextLeadAlreadyExists =
            contextLeadId && this.associatedLeads.some((lead) => lead.id === contextLeadId);

        if (
            this.customerId === customerId &&
            this.associatedLeads.length &&
            (!contextLeadId || contextLeadAlreadyExists)
        ) {
            return Promise.resolve();
        }

        this.customerId = customerId;
        const requestGeneration = (associatedLeadsRequestGenerations.get(this) ?? 0) + 1;
        associatedLeadsRequestGenerations.set(this, requestGeneration);

        const requestPromise = this.fetchAssociatedLeads(customerId, requestGeneration).finally(
            () => {
                if (associatedLeadsRequests.get(this)?.promise === requestPromise) {
                    associatedLeadsRequests.delete(this);
                }
            }
        );

        associatedLeadsRequests.set(this, { customerId, promise: requestPromise });

        return requestPromise;
    },

    async fetchAssociatedLeads(
        customerId = this.customerId,
        requestGeneration = associatedLeadsRequestGenerations.get(this)
    ) {
        if (!customerId) {
            throw new InvalidPayload('ID not provided.');
        }

        if (
            this.customerId === customerId &&
            associatedLeadsRequestGenerations.get(this) === requestGeneration
        ) {
            this.associatedLeads = [];
        }

        try {
            const response = await axios.get(`v1/customers/${customerId}/associatedLeads`);

            const associatedLeads = response.data.data
                .map((lead) => new Lead(lead))
                .sort((b, a) => {
                    return a.contextDate.diffInMilliseconds(b.contextDate);
                });

            if (
                this.customerId === customerId &&
                associatedLeadsRequestGenerations.get(this) === requestGeneration
            ) {
                this.associatedLeads = associatedLeads;
            }
        } catch (error) {
            // Glitch with the associated lead creation flow which is executed in 2 steps
        }
    },

    removeLead(leadId) {
        const leadIdIndex = this.associatedLeads.findIndex((lead) => lead.id === leadId);

        if (leadIdIndex === -1) {
            return;
        }

        this.associatedLeads.splice(leadIdIndex, 1);
    },

    updateLead(updatedLead) {
        const associatedLead = this.associatedLeads.find((lead) => lead.id === updatedLead.id);

        if (!associatedLead) {
            return;
        }

        merge(associatedLead, updatedLead);
    },

    updateLeadVehicle(updatedLeadVehicle) {
        if (!updatedLeadVehicle.id || !updatedLeadVehicle.lead_id) {
            return;
        }

        const associatedLead = this.associatedLeads.find(
            (lead) => lead.id === updatedLeadVehicle.lead_id
        );

        if (!associatedLead) {
            return;
        }

        const associatedLeadVehicle = associatedLead.vehicles.find(
            (vehicle) => vehicle.id === updatedLeadVehicle.id
        );

        if (!associatedLeadVehicle) {
            return;
        }

        merge(associatedLeadVehicle, updatedLeadVehicle);
    },
};
